import { Region, RoyaltyModelsEnum, Territory } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { RateCardCoreRateGridColumnName, RateMultiplierDisplayName } from 'src/constants';
import { CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP } from 'src/constants/rateCards';
import { IFullRateGridRowData, IRateCardCoreRateGridRowData, IThirdPartyRateGridRowData } from 'src/types/IRateCard';
import { getPromoDurationDisplayNumber } from 'src/util/PaperPlans/PaperPlanUtils';

export enum CalcType {
    AutoCalc = 'AutoCalc',
    User = 'User',
    Derived = 'Derived',
    SelectType = 'Select Type'
}

export const isPositiveNumber = (value: any) => {
    return !isNaN(value) && value > 0;
};

export const getRevenueCalcType = (wholesalePrice: any, endUserPrice: any, revDiscCap: any) => {
    if (isPositiveNumber(wholesalePrice) && isPositiveNumber(endUserPrice) && isPositiveNumber(revDiscCap)) {
        return CalcType.AutoCalc;
    } else {
        return CalcType.User;
    }
};

export const calcMultiplierName = (thirdPartyRate: IThirdPartyRateGridRowData) => {
    let multiplierName = '';
    if (thirdPartyRate.rateCalcType === CalcType.Derived && isIntroPlan(thirdPartyRate.rateGroup)) {
        switch (getPromoDurationDisplayNumber(thirdPartyRate.promoDurationInDays)) {
            case 60:
                multiplierName = RateMultiplierDisplayName.EXTENDED_TRIAL_2M;
                break;
            case 90:
                multiplierName = RateMultiplierDisplayName.EXTENDED_TRIAL_3M;
                break;
            case 120:
                multiplierName = RateMultiplierDisplayName.EXTENDED_TRIAL_4M;
                break;
            case 150:
                multiplierName = RateMultiplierDisplayName.EXTENDED_TRIAL_5M;
                break;
            case 180:
                multiplierName = RateMultiplierDisplayName.EXTENDED_TRIAL_6M;
                break;
            default:
                multiplierName = '';
        }
    } else if (thirdPartyRate.rateCalcType === CalcType.Derived && !isIntroPlan(thirdPartyRate.rateGroup)) {
        multiplierName = RateMultiplierDisplayName.NONE;
    }
    thirdPartyRate.multiplierName = multiplierName;
};

export const autoCalculateFinalRate = (thirdPartyRate: IThirdPartyRateGridRowData) => {
    let finalRate = thirdPartyRate.finalRate;
    if (!isIntroPlan(thirdPartyRate.planGroup) && thirdPartyRate.rateCalcType === CalcType.AutoCalc) {
        finalRate = (
            (thirdPartyRate.corePlanPSM as unknown as number) *
            (1 -
                Math.min(
                    thirdPartyRate.psmDiscCap as unknown as number,
                    // @ts-ignore
                    absoluteOfWholeAndUserPrice(thirdPartyRate.wholesalePrice, thirdPartyRate.endUserPrice)
                ))
        ).toString();
    } else if (thirdPartyRate.rateCalcType === CalcType.Derived) {
        // @ts-ignore
        finalRate = (thirdPartyRate.corePlanPSM * thirdPartyRate.multiplierValue).toString();
    }
    thirdPartyRate.finalRate = finalRate;
};

export const calculateRateCalcType = (thirdPartyRate: IThirdPartyRateGridRowData) => {
    let calcType;
    if (thirdPartyRate.rateType === RoyaltyModelsEnum.CPP) {
        calcType = CalcType.User;
    } else if (isIntroPlan(thirdPartyRate.rateGroup)) {
        if (isPositiveNumber(thirdPartyRate.corePlanPSM)) {
            calcType = CalcType.Derived;
        } else {
            calcType = CalcType.User;
        }
    } else {
        if (
            isPositiveNumber(thirdPartyRate.wholesalePrice) &&
            isPositiveNumber(thirdPartyRate.endUserPrice) &&
            isPositiveNumber(thirdPartyRate.corePlanPSM) &&
            isPositiveNumber(thirdPartyRate.psmDiscCap)
        ) {
            calcType = CalcType.AutoCalc;
        } else {
            calcType = CalcType.SelectType;
        }
    }
    thirdPartyRate.rateCalcType = calcType;
};

export const calculateRevCalcType = (thirdPartyRate: IThirdPartyRateGridRowData) => {
    let revCalcType = CalcType.User;
    if (
        !isIntroPlan(thirdPartyRate.rateGroup) &&
        isPositiveNumber(thirdPartyRate.wholesalePrice) &&
        isPositiveNumber(thirdPartyRate.endUserPrice) &&
        isPositiveNumber(thirdPartyRate.revDiscCap)
    ) {
        revCalcType = CalcType.AutoCalc;
    } else if (isIntroPlan(thirdPartyRate.rateGroup) && isPositiveNumber(thirdPartyRate.wholesalePrice)) {
        revCalcType = CalcType.AutoCalc;
    }
    thirdPartyRate.revCalcType = revCalcType;
};

export const isIntroPlan = (carrierPlan: string) => {
    return carrierPlan === 'Carrier Intro';
};

export const getRateCalcTypeList = (carrierPlan: string) => {
    if (isIntroPlan(carrierPlan)) {
        return [CalcType.Derived, CalcType.User];
    } else {
        return [CalcType.AutoCalc, CalcType.User, CalcType.Derived];
    }
};

export const getRevenueCalcTypeList = () => {
    return [CalcType.AutoCalc, CalcType.User];
};

export const getCorePlanRate = (coreRates: IRateCardCoreRateGridRowData[], territory: string, corePlan: string) => {
    const coreRateForTerritory = coreRates.find(
        (coreRate) => coreRate[RateCardCoreRateGridColumnName.Territory] === territory
    );
    const columnName = CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP.get(corePlan);
    //@ts-ignore Since this is derived from core plan table the territory and column name should always exist
    return coreRateForTerritory[columnName];
};

export const absoluteOfWholeAndUserPrice = (wholeSalePrice: number, endUserPrice: number) => {
    return Math.abs((wholeSalePrice - endUserPrice) / endUserPrice);
};

const getStandardServiceRevenue = (wholesalePrice: number, endUserPrice: number, revDeductionCap: number) => {
    return endUserPrice * (1 - Math.min(revDeductionCap, absoluteOfWholeAndUserPrice(wholesalePrice, endUserPrice)));
};

export const calculateServiceRevenue = (thirdPartyRate: IThirdPartyRateGridRowData) => {
    let serviceRevenue = thirdPartyRate.serviceRev;
    if (thirdPartyRate.revCalcType === CalcType.AutoCalc && !isIntroPlan(thirdPartyRate.rateGroup)) {
        serviceRevenue = getStandardServiceRevenue(
            thirdPartyRate.wholesalePrice as unknown as number,
            thirdPartyRate.endUserPrice as unknown as number,
            thirdPartyRate.revDiscCap as unknown as number
        ).toString();
    } else if (thirdPartyRate.revCalcType === CalcType.AutoCalc && isIntroPlan(thirdPartyRate.rateGroup)) {
        serviceRevenue = thirdPartyRate.wholesalePrice;
    }
    thirdPartyRate.serviceRev = serviceRevenue;
};

export const getFullRateFor3PRate = (
    thirdPartyRate: IThirdPartyRateGridRowData,
    coreRate: IRateCardCoreRateGridRowData,
    territoryMapping: Record<number, Territory>,
    regionMapping: Record<number, Region>
) => {
    let regionId = 0;
    for (const [code, territory] of Object.entries(territoryMapping)) {
        if (territory.territoryCode === thirdPartyRate.territory) {
            regionId = territory.regionId ?? 0;
        }
    }

    const regionCode = regionMapping[regionId]?.regionCode ?? '';

    const newFullRate: IFullRateGridRowData = {
        territory: thirdPartyRate.territory,
        region: regionCode,
        rateCardCurrency: coreRate[RateCardCoreRateGridColumnName.RatecardCurrency] ?? '',
        settlementCurrency: coreRate[RateCardCoreRateGridColumnName.SettlementCurrency] ?? '',
        cae: coreRate[RateCardCoreRateGridColumnName.CAE] ?? '',
        displayName: thirdPartyRate.displayName,
        paperPlanName: thirdPartyRate.paperPlanName,
        rateGroup: thirdPartyRate.rateGroup,
        rateType: thirdPartyRate.rateType,
        rate: thirdPartyRate.finalRate,
        tier2Rate: '',
        tier3Rate: '',
        serviceRev: thirdPartyRate.serviceRev
    };

    return newFullRate;
};
